import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye, MapPin, BadgeCheck, Flame, Sparkles, Lock } from "lucide-react";
import { StreamingOnlyLabel } from "@/components/FoundingBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/lib/membership";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1637059880830-59a90102de77";

export default function ProfileCard({ profile, index = 0, onLike, onSuperLike }) {
  const { user } = useAuth();
  const [revealed, setRevealed] = useState(false);
  const paid = isPaid(user);
  const lockedByTier = !paid;
  const discreet = (profile.discreet_mode || lockedByTier) && !revealed;

  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08 }}
      onMouseMove={move}
      data-testid={`profile-card-${profile.id}`}
      className="spotlight-card group relative rounded-2xl border border-white/8 bg-surface overflow-hidden"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={profile.photo_url || FALLBACK_IMG}
          alt={profile.display_name}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
            discreet ? "blur-2xl scale-110" : ""
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
        {discreet && (
          lockedByTier ? (
            <Link
              to="/premium"
              data-testid={`unlock-${profile.id}`}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3"
            >
              <span className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center backdrop-blur-md bg-black/40 text-gold">
                <Lock size={18} strokeWidth={1.5} />
              </span>
              <span className="text-xs uppercase tracking-[0.25em] text-gold">Upgrade to reveal</span>
            </Link>
          ) : (
            <button
              data-testid={`reveal-${profile.id}`}
              onClick={() => setRevealed(true)}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3"
            >
              <span className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center backdrop-blur-md bg-black/30">
                <Eye size={20} strokeWidth={1.5} />
              </span>
              <span className="text-xs uppercase tracking-[0.25em] text-text-muted">Discreet — tap to reveal</span>
            </button>
          )
        )}
        {profile.is_premium && (
          <span className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-3 py-1 rounded-full backdrop-blur-sm">
            Premium
          </span>
        )}
        {profile.boosted && (
          <span data-testid={`boosted-${profile.id}`} className="absolute top-4 right-4 z-10 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-orange-300 border border-orange-400/40 px-3 py-1 rounded-full backdrop-blur-sm bg-orange-500/10">
            <Flame size={11} /> Boosted
          </span>
        )}
        {profile.super && (
          <span data-testid={`super-badge-${profile.id}`} className="absolute top-4 right-4 z-10 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/50 px-3 py-1 rounded-full backdrop-blur-sm bg-gold/10">
            <Sparkles size={11} /> Super Admire
          </span>
        )}
        <div className="absolute top-14 left-4 z-10"><StreamingOnlyLabel user={profile} testId={`streaming-only-${profile.id}`} /></div>
      </div>

      <div className="relative z-10 p-5 -mt-16">
        <div className="flex items-end justify-between">
          <div>
            <h3 className="font-display text-2xl leading-none flex items-center gap-2">
              <span>{profile.display_name}<span className="text-text-muted text-lg">, {profile.age}</span></span>
              {profile.online && <span data-testid={`online-${profile.id}`} className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" title="Online now" />}
              {profile.verified && <BadgeCheck size={18} className="text-emerald-400" data-testid={`verified-${profile.id}`} />}
              <VerifiedBadge user={profile} size="xs" testId={`id-verified-${profile.id}`} />
            </h3>
            {profile.location && (
              <p className="flex items-center gap-1 text-xs text-text-muted mt-2">
                <MapPin size={12} /> {profile.location}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onSuperLike && (
              <button
                data-testid={`superlike-${profile.id}`}
                onClick={() => onSuperLike(profile)}
                title="Super admire"
                className="w-10 h-10 rounded-full border border-gold/40 text-gold flex items-center justify-center transition-colors hover:bg-gold/10"
              >
                <Sparkles size={16} strokeWidth={1.5} />
              </button>
            )}
            <button
              data-testid={`like-${profile.id}`}
              onClick={() => onLike && onLike(profile)}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                profile.liked_by_me ? "border-wine bg-wine" : "border-white/15 hover:border-wine"
              }`}
            >
              <Heart size={16} fill={profile.liked_by_me ? "currentColor" : "none"} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {profile.tagline && <p className="text-sm text-text-main/80 mt-3 italic font-display text-lg">"{profile.tagline}"</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          {(profile.relationship_goals || []).slice(0, 2).map((g) => (
            <span key={g} className="text-[10px] uppercase tracking-[0.15em] text-gold border border-wine/30 px-2.5 py-1 rounded-full">
              {g}
            </span>
          ))}
          {(profile.kinks || []).slice(0, 2).map((k) => (
            <span key={k} className="text-[10px] uppercase tracking-[0.15em] text-text-muted border border-white/10 px-2.5 py-1 rounded-full">
              {k}
            </span>
          ))}
        </div>
        <Link
          to={`/profile/${profile.id}`}
          data-testid={`view-${profile.id}`}
          className="mt-5 block text-center text-xs uppercase tracking-[0.2em] py-3 border border-white/10 rounded-full hover:border-white/30 hover:bg-white/5 transition-colors"
        >
          View profile
        </Link>
      </div>
    </motion.div>
  );
}
